<template>
  <div
    class="st-list-item"
    :class="[
      { 'cursor-pointer': clickable },
      { 'selected-item': selected },
      { 'item-disabled': disabled },
    ]"
    @click="handleItemClick"
  >
    <em
      v-if="icon"
      class="custom-icons shrink-0"
      :style="getCustomIconStyle(iconPath(icon))"
    />
    <span
      v-if="title"
      class="st-list-item-title"
      >{{ title }}</span
    >
    <slot>
      <span
        v-if="text"
        class="st-list-item-text"
        >{{ text }}</span
      >
    </slot>
    <Badge
      v-if="showBadge"
      class="ml-auto"
      :icon="badge.icon"
      :text="badge.text"
      :class="{ 'bg-badge': showBadge }"
    />
    <ButtonCustom
      v-if="buttonText"
      type="important-custom"
      class="st-list-item-button"
      :text="buttonText"
      @click="handleButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import Badge from '@/components/elements/Badge';
import ButtonCustom from '@/components/elements/ButtonCustom';

const props = withDefaults(
  defineProps<{
    itemId?: string;
    clickable?: boolean;
    selected?: boolean;
    disabled?: boolean;
    icon?: string;
    title?: string;
    text?: string;
    buttonText?: string;
    badge?: {
      text: string;
      icon?: string;
    };
  }>(),
  {
    itemId: '',
    clickable: true,
    selected: false,
    disabled: false,
    icon: '',
    title: '',
    text: '',
    buttonText: '',
    badge: () => ({ text: '' }),
  }
);
const emit = defineEmits(['itemClick', 'buttonClick']);

const { iconPath, getCustomIconStyle } = useCustomIcon();

const showBadge = computed(() => !!props.badge.text);

const handleItemClick = () => {
  if (props.clickable) {
    emit('itemClick', props.itemId);
  }
};

const handleButtonClick = () => {
  if (props.disabled) return;
  emit('buttonClick', props.itemId);
};
</script>

<style lang="scss">
.st-list-item {
  border: 1px solid;
  @apply flex items-center gap-2 py-2 px-4 border-standard-border bg-standard-bg rounded-lg box-border overflow-hidden w-full;

  .st-list-item-title,
  .st-list-item-text {
    @apply text-sm text-text-default;
  }

  .st-list-item-title {
    @apply font-medium;
  }

  .st-list-item-text {
    @apply truncate;
  }

  .st-list-item-button {
    @apply w-fit h-6 rounded py-0.5 px-2 box-border ml-auto shrink-0;

    .button-label {
      @apply text-sm;
    }
  }

  &.selected-item {
    box-shadow: 0 0 0 4px var(--blue400);
    @apply border-transparent;
  }

  &.item-disabled {
    .st-list-item-title,
    .st-list-item-text {
      @apply text-inactive-text;
    }
  }
}
</style>
